import {
  ModalState,
  ModalStateFactory
} from 'containers/Common/ModalStateFactory';
import {
  AddLimitationState,
  AddLimitationSummaryState,
  RemoveLimitationState,
  RemoveLimitationSummaryState
} from './LimitationManagementStates';
import { AddonFeatureManager, LocaleMeta } from 'core';
import { L1Object } from 'core/l1Object/L1Object';
import { Order } from 'core/order/Order';
import { CreateRtbCampaignSetupFlowPageModel } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignSetupFlowPageModel';
import { BidStrategy } from 'core/l2Object/L2Object';
import { EditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import { OPERATE } from 'enum/Operate';
import {
  LimitationManagementContext,
  RtbCampaignContext
} from './LimitationManagementContext';

export enum LimitationManagementAction {
  BatchAddLimitation = 'addLimitation',
  BatchRemoveLimitation = 'removeLimitation'
}

export class DefaultLimitationManagementStateFactory implements ModalStateFactory<LimitationManagementContext> {
  constructor(
    protected order: Order,
    protected l1Object: L1Object,
    protected action: string,
    protected campaigns: RtbCampaignContext[],
    private addonFeatureManager: AddonFeatureManager,
    protected localeMeta?: LocaleMeta
  ) {}

  async initStates(
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean, beforeClose?: () => Promise<void>) => void,
    getContextData: () => LimitationManagementContext | undefined
  ) {
    // 不同 adType 需要的 limitation model 產生邏輯已經實作在 campaignBasicFormModel
    // 所以拿既有的 model 來初始化，之後要想一下怎麼優化這段
    const campaignCreateModel = new CreateRtbCampaignSetupFlowPageModel(
      this.order,
      this.addonFeatureManager,
      this.l1Object
    );
    const initCampaign = await campaignCreateModel.getCampaign();
    campaignCreateModel.campaign = {
      basic: {
        ...initCampaign.basic,
        adType: this.campaigns[0].adType,
        ageMax: 65,
        ageMin: 18,
        messages: '',
        bidStrategy: BidStrategy.LOWEST_COST_WITHOUT_CAP,
        bindings: []
      },
      limitations: initCampaign.limitations
    };
    campaignCreateModel.createCampaignFormModels();
    await campaignCreateModel.setUpCampaignBasicFormModel(
      this.campaigns[0].adType,
      this.campaigns[0].adTypeId,
      false
    );
    const limitationModel = campaignCreateModel.getLimitationModel(false);
    limitationModel.setSupportAudienceEstimation(false);
    limitationModel.setOperationSet({
      need: [OPERATE.INCLUDE, OPERATE.PREFERRED, OPERATE.NONPREFERRED],
      notNeed: [OPERATE.EXCLUDE],
      other: undefined
    });
    const campaignBasicFormModel = campaignCreateModel.campaignBasicFormModel;
    const supportAgeGenderLimitation = campaignBasicFormModel
      ? campaignBasicFormModel.limitationConfig.supportAgeGenderLimitation
      : false;

    switch (this.action) {
      case LimitationManagementAction.BatchAddLimitation:
        return this.initAddLimitationStates(
          limitationModel,
          supportAgeGenderLimitation,
          goModalStateFn,
          closeModalFn,
          getContextData
        );
      default:
        return this.initRemoveLimitationStates(
          limitationModel,
          goModalStateFn,
          closeModalFn,
          getContextData
        );
    }
  }

  async initAddLimitationStates(
    limitationModel: EditLimitationModel,
    supportAgeGenderLimitation: boolean,
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean, beforeClose?: () => Promise<void>) => void,
    getContextData: () => LimitationManagementContext | undefined
  ) {
    const modifyLimitationState = new AddLimitationState(
      this.order,
      this.campaigns,
      limitationModel,
      supportAgeGenderLimitation,
      getContextData
    );
    const limitationSummaryState = new AddLimitationSummaryState(
      this.campaigns,
      limitationModel.limitationSetting,
      supportAgeGenderLimitation,
      getContextData,
      this.localeMeta
    );
    modifyLimitationState.setNextFn(() =>
      goModalStateFn(limitationSummaryState)
    );
    modifyLimitationState.setCloseFn(closeModalFn);
    limitationSummaryState.setBackFn(() =>
      goModalStateFn(modifyLimitationState)
    );
    limitationSummaryState.setCloseFn(closeModalFn);
    return modifyLimitationState;
  }

  async initRemoveLimitationStates(
    limitationModel: EditLimitationModel,
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean, beforeClose?: () => Promise<void>) => void,
    getContextData: () => LimitationManagementContext | undefined
  ) {
    const modifyLimitationState = new RemoveLimitationState(
      this.order,
      this.campaigns,
      limitationModel,
      getContextData
    );
    const limitationSummaryState = new RemoveLimitationSummaryState(
      this.campaigns,
      limitationModel.limitationSetting,
      false,
      getContextData,
      this.localeMeta
    );
    modifyLimitationState.setNextFn(() =>
      goModalStateFn(limitationSummaryState)
    );
    modifyLimitationState.setCloseFn(closeModalFn);
    limitationSummaryState.setBackFn(() =>
      goModalStateFn(modifyLimitationState)
    );
    limitationSummaryState.setCloseFn(closeModalFn);
    return modifyLimitationState;
  }
}
